import "../../kendo.icons.js";
import { inject } from "../../core/service-container";
import { HtmlService } from "../../core/services/html.service";
import { DateParserService } from "../../core/services/date-parser.service";
import { FormatterService } from "../../core/services/formatter.service";
import { DateUtilsService } from "../../core/services/date-utils.service";
import { UtilsService } from "../../core/services/utils.service";
import { STYLES, ICONS, REFERENCES, MESSAGE_STATUS, FILES_LAYOUT_MODE } from "../constants";
import type { IMessage, IAuthor, IChatMessages, IChatAttachment, TimestampTemplateFunction, MessageTemplateFunction, MessageStatusTemplateFunction, MessageContentTemplateFunction, AttachmentTemplateFunction, UserStatusTemplateFunction, MessageStatus, IMessageSettings, IMessageStatusSettings, FilesLayoutMode, AttachmentLayoutMode } from "../models";
import { renderAvatar, renderTypingIndicator } from "./common.template";
import { renderFiles, renderMessageReference } from "./file.template";

declare const kendo: any;

/**
 * Renders the retry button for failed messages.
 */
export const renderRetryButton = (
    message: IMessage,
    retryText: string
): string => {
    if (!message.failed) {
        return "";
    }
    return kendo.html.renderButton(
        `<button class="${STYLES.messageRetryButton}" ${REFERENCES.retryButton} title="${retryText}" aria-label="${retryText}" data-uid="${message.uid || ""}"></button>`,
        { icon: ICONS.retry, size: "small", fillMode: "flat" }
    );
};

/**
 * Renders the message status indicator.
 * Supports custom status settings with icon/svgIcon for enhanced display.
 */
export const renderMessageStatus = (
    status: MessageStatus | undefined,
    message: IMessage,
    statusTemplate?: MessageStatusTemplateFunction | null,
    htmlService = inject(HtmlService),
    statusSettings?: Record<MessageStatus, IMessageStatusSettings>
): string => {
    if (!status) {
        return "";
    }

    if (statusTemplate) {
        return statusTemplate({ status, message });
    }

    const settings = statusSettings?.[status];
    const statusClass = status === MESSAGE_STATUS.FAILED
        ? `${STYLES.messageStatus} ${STYLES.messageFailed}${settings?.cssClass ? " " + settings.cssClass : ""}`
        : `${STYLES.messageStatus}${settings?.cssClass ? " " + settings.cssClass : ""}`;

    let iconHtml = "";
    if (settings?.icon) {
        iconHtml = kendo.ui.icon({ icon: settings.icon });
    } else if (settings?.svgIcon) {
        iconHtml = kendo.ui.icon({ icon: settings.svgIcon });
    }

    const statusText = settings?.text !== undefined
        ? settings.text
        : status.charAt(0).toUpperCase() + status.slice(1);

    return `<span class="${statusClass}">${iconHtml}${htmlService.encode(statusText)}</span>`;
};

/**
 * Renders a single attachment card.
 */
export const renderAttachment = (
    attachment: IChatAttachment,
    message: IMessage,
    attachmentTemplate?: AttachmentTemplateFunction | null,
    htmlService = inject(HtmlService)
): string => {
    if (attachmentTemplate) {
        return attachmentTemplate({ attachment, message });
    }

    const title = attachment.title ? `<div class="${STYLES.cardTitle}">${htmlService.encode(attachment.title)}</div>` : "";
    const subtitle = attachment.subtitle ? `<div class="${STYLES.cardSubtitle}">${htmlService.encode(attachment.subtitle)}</div>` : "";
    const image = attachment.thumbnailUrl ? `<div class="${STYLES.cardMedia}"><img src="${htmlService.encode(attachment.thumbnailUrl)}" alt="" /></div>` : "";
    const actions = attachment.actions?.length
        ? `<div class="${STYLES.cardActions}">${attachment.actions.map(a => `<button class="${STYLES.cardAction} ${STYLES.button}">${htmlService.encode(a.text)}</button>`).join("")}</div>`
        : "";

    return `<div class="${STYLES.card}">
        ${image}
        <div class="${STYLES.cardBody}">
            ${title}
            ${subtitle}
        </div>
        ${actions}
    </div>`;
};

/**
 * Renders attachments with the specified layout.
 */
export const renderAttachments = (
    attachments: IChatAttachment[] | undefined,
    message: IMessage,
    layout: AttachmentLayoutMode = "list",
    attachmentTemplate?: AttachmentTemplateFunction | null
): string => {
    if (!attachments?.length) {
        return "";
    }

    const attachmentsHtml = attachments.map(a => renderAttachment(a, message, attachmentTemplate)).join("");
    const layoutClass = layout === "carousel" ? STYLES.cardDeckScrollWrap : STYLES.cardList;

    return `<div class="${layoutClass}">${attachmentsHtml}</div>`;
};

/**
 * Renders a single chat message.
 */
export const renderMessage = (
    message: IMessage & { isOwnMessage: boolean; author: IAuthor },
    replyMessage: IMessage | null,
    downloadAll: boolean,
    messages: IChatMessages,
    expandable: boolean,
    messageTimeFormat: string,
    skipSanitization: boolean,
    statusTemplate?: MessageStatusTemplateFunction | null,
    filesLayoutMode: FilesLayoutMode = FILES_LAYOUT_MODE.VERTICAL,
    htmlService = inject(HtmlService),
    formatterService = inject(FormatterService),
    dateParserService = inject(DateParserService),
    contentTemplate?: MessageContentTemplateFunction | null,
    attachmentTemplate?: AttachmentTemplateFunction | null,
    attachmentLayout?: AttachmentLayoutMode
): string => {
    const isDeleted = message.isDeleted;
    const isFailed = message.failed || message.status === MESSAGE_STATUS.FAILED;
    const expandableClasses = expandable && !message.isTyping
        ? [STYLES.bubbleExpandable, STYLES.expanded]
        : [];

    const replyMessageHtml = replyMessage
        ? renderMessageReference({
            text: replyMessage.text,
            files: replyMessage.files,
            isOwnMessage: replyMessage.isOwnMessage,
            isPinMessage: false,
            renderCloseButton: false,
            renderFileMenuButton: false
        })
        : "";

    let messageContent = "";
    if (message.isTyping && !message.isOwnMessage) {
        messageContent = renderTypingIndicator();
    } else if (contentTemplate && !isDeleted) {
        messageContent = contentTemplate(message);
    } else if (isDeleted) {
        messageContent = message.isOwnMessage
            ? htmlService.encode(messages.selfMessageDeleted)
            : htmlService.encode(messages.otherMessageDeleted);
    } else {
        messageContent = htmlService.convertTextUrlToLink(message.text || "", skipSanitization);
    }

    const attachmentsHtml = !isDeleted
        ? renderAttachments(message.attachments, message, attachmentLayout || message.attachmentLayout || "list", attachmentTemplate)
        : "";

    const timeHtml = `<time class="${STYLES.messageTime}">${formatterService.toString(dateParserService.parseDate(message.timestamp), messageTimeFormat)}</time>`;
    const statusHtml = renderMessageStatus(message.status, message, statusTemplate, htmlService);
    const retryHtml = isFailed ? renderRetryButton(message, messages.retryMessage || "Retry") : "";
    const messageInfoHtml = `<div class="${STYLES.messageInfo}">${timeHtml}${statusHtml}${retryHtml}</div>`;

    return `<div class="${STYLES.message}${isDeleted ? " " + STYLES.messageRemoved : ""}${isFailed ? " " + STYLES.messageFailed : ""}" data-uid="${htmlService.encode(message.uid ?? "")}">
        <div class="${STYLES.chatBubble} ${STYLES.bubble} ${expandableClasses.join(" ")}">
            <div class="${STYLES.bubbleContent}">
                ${replyMessageHtml}
                <span class="${STYLES.chatBubbleText}">${messageContent}</span>
                ${!isDeleted ? renderFiles(message.files, downloadAll, messages, false, filesLayoutMode) : ""}
                ${attachmentsHtml}
            </div>
            ${expandable && !message.isTyping ? `<span class="${STYLES.bubbleExpandableIndicator}" ${REFERENCES.bubbleExpandableIndicator}>${kendo.ui.icon({ icon: ICONS.chevronUp })}</span>` : ""}
        </div>
        ${messageInfoHtml}
        ${!isDeleted ? `<div class="${STYLES.messageToolbar}"></div>` : ""}
    </div>`;
};

/**
 * Renders a message group with author avatar and multiple messages.
 */
export const renderMessageGroup = (
    context: {
        message: IMessage;
        author: IAuthor;
        isOwnMessage: boolean;
        replyMessage?: IMessage | null;
        downloadAll?: boolean;
        messages?: IChatMessages;
        expandable?: boolean;
        fullWidth?: boolean;
        messageTimeFormat?: string;
        timestampTemplate?: TimestampTemplateFunction | null;
        statusTemplate?: MessageStatusTemplateFunction | null;
        showTimestamp?: boolean;
        messageTemplate?: MessageTemplateFunction;
        skipSanitization?: boolean;
        messageSettings?: IMessageSettings;
        filesLayoutMode?: FilesLayoutMode;
        contentTemplate?: MessageContentTemplateFunction | null;
        authorMessageContentTemplate?: MessageContentTemplateFunction | null;
        receiverMessageContentTemplate?: MessageContentTemplateFunction | null;
        attachmentTemplate?: AttachmentTemplateFunction | null;
        attachmentLayout?: AttachmentLayoutMode;
        userStatusTemplate?: UserStatusTemplateFunction | null;
    },
    utilsService = inject(UtilsService),
    dateUtilsService = inject(DateUtilsService),
    htmlService = inject(HtmlService),
    dateParserService = inject(DateParserService)
): string => {
    const {
        message,
        author,
        isOwnMessage,
        replyMessage,
        downloadAll = true,
        messages,
        expandable = false,
        fullWidth = false,
        messageTimeFormat = "ddd MMM dd yyyy",
        timestampTemplate,
        statusTemplate,
        showTimestamp = false,
        messageTemplate,
        skipSanitization = false,
        messageSettings,
        filesLayoutMode = FILES_LAYOUT_MODE.VERTICAL,
        contentTemplate,
        authorMessageContentTemplate,
        receiverMessageContentTemplate,
        attachmentTemplate,
        attachmentLayout,
        userStatusTemplate
    } = context;

    // Determine the effective content template based on message author
    const effectiveContentTemplate = isOwnMessage
        ? (authorMessageContentTemplate || contentTemplate)
        : (receiverMessageContentTemplate || contentTemplate);

    // Apply user-specific settings if provided
    const effectiveShowAvatar = messageSettings?.showAvatar !== undefined
        ? messageSettings.showAvatar && author && author.imageUrl
        : author && author.imageUrl;
    const effectiveShowUsername = messageSettings?.showUsername !== undefined
        ? messageSettings.showUsername
        : true;
    const effectiveExpandable = messageSettings?.allowMessageCollapse !== undefined
        ? messageSettings.allowMessageCollapse
        : expandable;
    const effectiveFullWidth = messageSettings?.messageWidthMode === "full" || fullWidth;

    const groupClasses = [
        STYLES.messageGroup,
        isOwnMessage ? STYLES.messageGroupSender : STYLES.messageGroupReceiver,
        effectiveShowAvatar ? "" : STYLES.noAvatar,
        effectiveFullWidth ? STYLES.messageGroupFullWidth : ""
    ].filter(Boolean).join(" ");

    let timestampContent = "";
    if (showTimestamp && message.timestamp) {
        const messageDate = dateParserService.parseDate(message.timestamp);
        if (utilsService.isFunction(timestampTemplate)) {
            timestampContent = timestampTemplate!({ date: messageDate, message });
        } else {
            const relativeDateText = dateUtilsService.getRelativeDateString(messageDate);
            timestampContent = `<div class="${STYLES.timestamp}">${relativeDateText}</div>`;
        }
    }

    const userStatusHtml = userStatusTemplate ? userStatusTemplate({ author }) : "";

    // Use custom messageTemplate or default renderMessage
    let messageHtml: string;
    if (messageTemplate) {
        messageHtml = messageTemplate(
            { ...message, isOwnMessage, author },
            replyMessage ?? null,
            downloadAll,
            messages!,
            effectiveExpandable,
            messageTimeFormat,
            skipSanitization
        );
    } else {
        messageHtml = renderMessage(
            { ...message, isOwnMessage, author },
            replyMessage ?? null,
            downloadAll,
            messages!,
            effectiveExpandable,
            messageTimeFormat,
            skipSanitization,
            statusTemplate,
            filesLayoutMode,
            htmlService,
            undefined,
            undefined,
            effectiveContentTemplate,
            attachmentTemplate,
            attachmentLayout
        );
    }

    return `${showTimestamp && timestampContent ? timestampContent : ""}
        <div class="${groupClasses}">${userStatusHtml}
            ${effectiveShowAvatar ? renderAvatar(author.imageUrl!, author.imageAltText) : ""}
            <div class="${STYLES.messageGroupContent}">
                ${effectiveShowUsername ? `<span class="${STYLES.messageAuthor}">${htmlService.encode(author.name ?? "")}</span>` : ""}
                ${messageHtml}
            </div>
        </div>`;
};
